/*
 * Welcome discount — applies the signup discount code to the visitor's session
 * and keeps it alive across page loads.
 *
 * Loaded on every page (not just the homepage where the popup lives) so the
 * once-per-session self-heal can re-assert the discount if the cookie expires
 * or the cart attribute is dropped.
 */
(function () {
  const KEYS = {
    subscribed: 'hakeem_welcome_subscribed',
    code: 'hakeem_welcome_discount',
    session: 'hakeem_welcome_session',
    pending: 'hakeem_welcome_pending',
    navigated: 'hakeem_welcome_navigated',
  };

  // Visible (no underscore prefix) so it shows on the order as an audit trail
  // of which orders came from a popup signup.
  const CART_ATTRIBUTE = 'Welcome discount';

  const ACTIVE_CLASS = 'welcome-discount-active';

  const store = {
    get(storage, key) {
      try {
        return window[storage].getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(storage, key, value) {
      try {
        window[storage].setItem(key, value);
      } catch (e) {
        /* private mode, blocked storage — the cart attribute is the fallback */
      }
    },
    remove(storage, key) {
      try {
        window[storage].removeItem(key);
      } catch (e) {
        /* no-op */
      }
    },
  };

  function isDesignMode() {
    // In the theme editor the storefront runs in an admin iframe, where the
    // shop's cookies are third-party and get blocked. The discount would
    // silently no-op, so don't pretend otherwise.
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function cartUpdateUrl() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update.js';
  }

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  /*
   * Hits the storefront discount route, which sets the session discount cookie.
   * `redirect=/cart.js` makes the 302 land on a few hundred bytes of cart JSON
   * instead of the full homepage, and gives us the cart state back in the same
   * round trip so we can verify the discount actually landed.
   */
  function applyDiscount(code) {
    return fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`discount request failed: ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        const applications = cart.cart_level_discount_applications || [];
        return {
          cart,
          // A disabled, expired or ineligible code redirects exactly like a
          // working one, so only a cart WITH items can disprove it. With an
          // empty cart there is nothing to check against.
          confirmed: cart.item_count === 0 || applications.length > 0,
        };
      });
  }

  function writeCartAttribute(code) {
    return fetch(cartUpdateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { [CART_ATTRIBUTE]: code } }),
    }).catch(() => {
      /* the localStorage driver still carries the banner */
    });
  }

  /*
   * Swaps .drawer__inner only. #CartDrawer-Overlay is a SIBLING of it
   * (snippets/cart-drawer.liquid:26 vs :27) and its click listener is bound
   * once in the CartDrawer constructor (assets/cart-drawer.js:6) — replacing
   * #CartDrawer's innerHTML instead would destroy it.
   */
  function refreshDrawer() {
    const target = document.querySelector('cart-drawer .drawer__inner');
    if (!target) return Promise.resolve();

    return fetch(`${cartUrl()}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((text) => {
        const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('.drawer__inner');
        if (fresh) target.innerHTML = fresh.innerHTML;
      })
      .catch(() => {
        /* the drawer refreshes on its own next open */
      });
  }

  /*
   * Records that this visitor subscribed, without claiming the discount has
   * landed yet. Called the instant we know they signed up — BEFORE the async
   * apply — because the shopper can close the popup and navigate away while
   * those fetches are still in flight, which cancels them. Persisting first
   * means selfHeal() retries on the next page load instead of the signup being
   * silently lost.
   */
  function remember(code) {
    store.set('localStorage', KEYS.subscribed, '1');
    store.set('localStorage', KEYS.code, code);
  }

  function showBanner(code) {
    remember(code);
    document.documentElement.classList.add(ACTIVE_CLASS);
  }

  function hideBanner() {
    store.remove('localStorage', KEYS.code);
    document.documentElement.classList.remove(ACTIVE_CLASS);
  }

  /*
   * Falls back to the documented top-level discount link when the fetch route
   * fails. Guarded by a session flag so it can never loop.
   */
  function navigateFallback(code) {
    if (store.get('sessionStorage', KEYS.navigated)) return false;

    store.set('sessionStorage', KEYS.navigated, '1');
    store.set('localStorage', KEYS.subscribed, '1');
    store.set('localStorage', KEYS.code, code);
    window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/`;
    return true;
  }

  function activate(code) {
    if (!code || isDesignMode()) return Promise.resolve(false);

    return applyDiscount(code)
      .then((result) => {
        if (!result.confirmed) {
          // We can see it isn't landing — don't claim a discount that won't apply.
          hideBanner();
          return false;
        }

        return writeCartAttribute(code)
          .then(() => {
            showBanner(code);
            // Only worth refreshing when there are items whose totals changed.
            return result.cart.item_count > 0 ? refreshDrawer() : null;
          })
          .then(() => true);
      })
      .catch(() => {
        navigateFallback(code);
        return false;
      });
  }

  /*
   * Runs on every page load for anyone who has already subscribed. Re-applying
   * an already-applied code is a no-op — usage is counted at order time, not
   * when the cookie is set. Costs one small fetch per session and covers the
   * discount cookie expiring, the cart attribute being dropped when the cart
   * empties, and a failed first attempt.
   */
  function selfHeal() {
    if (isDesignMode()) return;
    if (!store.get('localStorage', KEYS.subscribed)) return;
    if (store.get('sessionStorage', KEYS.session)) return;

    store.set('sessionStorage', KEYS.session, '1');

    const code = store.get('localStorage', KEYS.code);
    if (code) activate(code);
  }

  window.HakeemWelcomeDiscount = { KEYS, CART_ATTRIBUTE, activate, isDesignMode, remember, showBanner };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', selfHeal);
  } else {
    selfHeal();
  }
})();
